import React from 'react';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { LoadingSpinner, ChartGuideModal } from '../components/ui';
import LicenseGate from '../components/ui/LicenseGate';
import useChartGuide from '../hooks/useChartGuide';
import { logger } from '../utils/logger';
import {
  KPIsSection,
  CustomerClubPerformanceFunnel,
  AverageCreditUsageRate,
  KeyInsights,
  PerformanceTable
} from '../components/executive';
import { TrendAnalysisChart, CreditComparisonChart } from '../components/executive';

interface ExecutiveDashboardProps {
  selectedBrand?: { id: number; name: string; logo?: string } | null;
  selectedBranch?: { id: number; name: string; brandId: number } | null;
  currency:string,
  selectedPeriod?: string;
}

const ExecutiveDashboard: React.FC<ExecutiveDashboardProps> = ({
  selectedBrand,
  selectedBranch,
    currency,
  selectedPeriod
}) => {
  const { timeFrame } = useTimeFrame();
  const { isModalOpen, currentGuide, closeGuide } = useChartGuide();
  
  // Use selectedPeriod if provided, otherwise use the timeFrame from context
  const effectivePeriod = selectedPeriod || timeFrame;
  
  // Convert UI period to API period if selectedPeriod is provided
  const apiTimeRange = selectedPeriod ? {
    timeRange: selectedPeriod === 'last1Month' ? '1month' :
              selectedPeriod === 'last3Months' ? '3months' :
              selectedPeriod === 'last6Months' ? '6months' :
              selectedPeriod === 'lastYear' ? '12months' : '6months'
  } : undefined;
  
  // Handle error
  const handleError = (error: Error) => {
    logger.error('Executive Dashboard Error', 'ExecutiveDashboard', error);
  };
  
  if (!selectedBranch?.id) {
    return (
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-4 md:space-y-6 animate-enter-up">
          <div className="card-base card-padding border border-status-warning" role="alert">
            <h3 className="title-card text-status-warning">برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.</h3>
            <p className="text-text-secondary">برای مشاهده داشبورد مدیریتی، ابتدا باید یک شعبه را انتخاب نمایید.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LicenseGate licenseType="club" dashboardName="داشبورد مدیریتی باشگاه مشتریان">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Header */}
          <h1 className="title-dashboard">
            مدیریت باشگاه مشتریان
          </h1>

          {/* KPI Cards */}
          <KPIsSection 
            branchId={selectedBranch.id}
            timeRange={apiTimeRange}
            onError={handleError}
          />

          {/* Funnel Analysis and Analysis Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6"> */}
            <CustomerClubPerformanceFunnel
              branchId={selectedBranch.id}
              timeRange={apiTimeRange}
              onError={handleError}
            />

            {/* Analysis Panel */}
            {/* <div className="space-y-6">
              <AverageCreditUsageRate
                branchId={selectedBranch.id}
                timeRange={apiTimeRange}
                onError={handleError}
              />
              
              <KeyInsights
                branchId={selectedBranch.id}
                timeRange={apiTimeRange}
                onError={handleError}
              />
            </div> */}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
            <TrendAnalysisChart
              branchId={selectedBranch.id}
              timeRange={apiTimeRange}
              onError={handleError}
            />
            
            <CreditComparisonChart
              branchId={selectedBranch.id}
              timeRange={apiTimeRange}
              onError={handleError}
            />
          </div>

          {/* Data Table */}
          <PerformanceTable
            branchId={selectedBranch.id}
            currency={currency}
            timeRange={apiTimeRange}
            onError={handleError}
          />
          
          {/* Chart Guide Modal */}
          {currentGuide && (
            <ChartGuideModal
              isOpen={isModalOpen}
              onClose={closeGuide}
              title={currentGuide.title}
            >
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
                  <p className="text-text-secondary">{currentGuide.description}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
                  <p className="text-text-secondary">{currentGuide.interpretation}</p>
                </div>
                {currentGuide.tips.length > 0 && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                    <ul className="list-disc list-inside space-y-2">
                      {currentGuide.tips.map((tip, index) => (
                        <li key={index} className="text-text-secondary">{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ChartGuideModal>
          )}
        </div>
      </div>
    </LicenseGate>
  );
};

export default ExecutiveDashboard;